import { useEffect, useState, useRef } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import api, { getVerticals, getVertical, createVertical, updateVertical, deleteVertical, extractYouTubeTranscript, downloadTemplate, bulkImportVerticals, bulkImportVerticalsDocx, downloadWordTemplate, exportVerticals, importVerticalData } from "@/lib/api";
import { 
  Plus, Pencil, Trash2, Loader2, Quote, User, Building, Star, Image, X,
  IndianRupee, Clock, ArrowLeftRight, Package, Calculator, HelpCircle,
  FileText, GitCompare, Milestone, ChevronLeft, Save, Eye, Video, Play, ExternalLink,
  Download, Upload, FileUp, CheckCircle, AlertCircle, FileSpreadsheet, TrendingUp, Share2, FileDown
} from "lucide-react";
import { toast } from "sonner";
import { getIcon } from "@/lib/icons";

const iconOptions = [
  "ShoppingCart", "Factory", "Wheat", "Pill", "Cog", "Wind", "Egg", "Wine",
  "FlaskConical", "Car", "HardHat", "UtensilsCrossed", "GraduationCap", "Shield", "Building2"
];

const deliverableIcons = [
  "CheckCircle", "Package", "FileText", "Settings", "Users", "Headphones",
  "Shield", "Zap", "Target", "Award", "Clock", "Database"
];

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
const emptyTestimonial = { name: "", role: "", company: "", quote: "", image_url: "" };
const emptyBeforeAfter = { aspect: "", before: "", after: "" };
const emptyDeliverable = { title: "", description: "", icon: "CheckCircle" };
const emptyFAQ = { question: "", answer: "" };
const emptyBrochure = { title: "", description: "", file_url: "", thumbnail_url: "" };
const emptyCompetitor = { name: "", logo_url: "" };
const emptyVideo = { title: "", youtube_url: "", transcript_dataset_id: "" };

const getYouTubeVideoId = (url) => {
  if (!url) return '';
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : '';
};

export default function AdminVerticals() {
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVertical, setSelectedVertical] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newVerticalData, setNewVerticalData] = useState({ name: "", description: "", icon: "Building2", image_url: "", order: 0 });
  const [processingVideo, setProcessingVideo] = useState(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  
  // Bulk Import States
  const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const fetchVerticals = async () => {
    try {
      const res = await getVerticals();
      setVerticals(res.data);
    } catch (error) {
      toast.error("Failed to load verticals");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchVerticals(); }, []);
  
  // Bulk Import Functions
  const handleDownloadTemplate = async (templateType) => {
    try {
      const res = await downloadTemplate(templateType);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', templateType === 'vertical' ? 'VERTICAL_TEMPLATE.json' : 'VERTICAL_EXAMPLE_PHARMA.json');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };
  
  const handleDownloadWordTemplate = async () => {
    try {
      const res = await downloadWordTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'ABSOLIN_Vertical_Template.docx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Word template downloaded!');
    } catch (error) {
      toast.error('Failed to download Word template');
    }
  };
  
  const handleExportVerticals = async () => {
    try {
      const res = await exportVerticals();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `verticals_export_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Verticals exported!');
    } catch (error) {
      toast.error('Failed to export verticals');
    }
  };

  // Market Pitch Playbook (.docx) — complete vertical data + Presentation / Brochure / Whitepaper frameworks
  const [downloadingPlaybook, setDownloadingPlaybook] = useState(false);
  const handleDownloadPlaybook = async () => {
    setDownloadingPlaybook(true);
    try {
      const res = await api.get("/admin/verticals/market-pitch-playbook", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `absolin_market_pitch_playbook_${new Date().toISOString().split('T')[0]}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Market Pitch Playbook downloaded — covers all verticals.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate playbook');
    }
    setDownloadingPlaybook(false);
  };

  // ========================================================
  // Environment-to-Environment Sync (preview ↔ production)
  // ========================================================
  const [envSyncOpen, setEnvSyncOpen] = useState(false);
  const [envSyncBusy, setEnvSyncBusy] = useState(false);
  const [envSyncResult, setEnvSyncResult] = useState(null);
  const [envSyncFile, setEnvSyncFile] = useState(null);

  const handleDownloadSnapshot = async () => {
    setEnvSyncBusy(true);
    try {
      const res = await api.get("/admin/verticals/export-full");
      const snapshot = res.data;
      const filename = `absolin-verticals-snapshot_${new Date().toISOString().split('T')[0]}.json`;
      const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Snapshot downloaded — ${snapshot.counts.verticals} verticals, ${snapshot.counts.problem_statements} problems, ${snapshot.counts.solutions ?? 0} solutions, ${snapshot.counts.case_studies ?? 0} case studies, ${snapshot.counts.client_logos} logos`);
    } catch (e) {
      toast.error("Export failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setEnvSyncBusy(false);
    }
  };

  const handleUploadSnapshot = async (mode, dryRun) => {
    if (!envSyncFile) { toast.error("Select a snapshot JSON file first"); return; }
    setEnvSyncBusy(true);
    setEnvSyncResult(null);
    try {
      const text = await envSyncFile.text();
      const snapshot = JSON.parse(text);
      if (!snapshot.verticals || !Array.isArray(snapshot.verticals)) {
        toast.error("Invalid snapshot — missing 'verticals' array");
        return;
      }
      const res = await api.post("/admin/verticals/import-full", { snapshot, mode, dry_run: dryRun });
      setEnvSyncResult(res.data);
      if (dryRun) {
        toast.success("Dry run complete — review results below, then re-run without dry-run");
      } else {
        toast.success(`Import complete — ${res.data.actions.verticals_inserted} inserted, ${res.data.actions.verticals_updated} updated`);
        fetchVerticals();
      }
    } catch (e) {
      toast.error("Import failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setEnvSyncBusy(false);
    }
  };
  
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const isDocx = file.name.toLowerCase().endsWith('.docx');
    const isJson = file.name.toLowerCase().endsWith('.json');
    
    if (!isDocx && !isJson) {
      toast.error('Please upload a .docx (Word) or .json file');
      return;
    }
    
    setUploading(true);
    setImportResult(null);
    
    try {
      // If in edit mode, import to current vertical
      if (isEditing && selectedVertical?.id) {
        const formData = new FormData();
        formData.append('file', file);
        
        // Import to specific vertical
        const res = await importVerticalData(selectedVertical.id, file);
        setImportResult(res.data);
        
        if (res.data.success) {
          toast.success('Data imported successfully!');
          // Refresh the vertical data
          const updatedVertical = await getVertical(selectedVertical.id);
          setSelectedVertical(updatedVertical.data);
          setFormData({
            name: updatedVertical.data.name || "",
            description: updatedVertical.data.description || "",
            icon: updatedVertical.data.icon || "Building2",
            image_url: updatedVertical.data.image_url || "",
            order: updatedVertical.data.order || 0,
            testimonials: updatedVertical.data.testimonials || [],
            investment_range: updatedVertical.data.investment_range || "",
            total_duration: updatedVertical.data.total_duration || "",
            before_after: updatedVertical.data.before_after || [],
            deliverables: updatedVertical.data.deliverables || [],
            roi_calculator: updatedVertical.data.roi_calculator || { enabled: false, metrics: [] },
            faq: updatedVertical.data.faq || [],
            brochures: updatedVertical.data.brochures || [],
            videos: updatedVertical.data.videos || [],
            competitor_comparison: updatedVertical.data.competitor_comparison || { enabled: false, competitors: [], features: [] },
            implementation_timeline: updatedVertical.data.implementation_timeline || { enabled: false, phases: [] },
            value_delivered: updatedVertical.data.value_delivered || {}
          });
          fetchVerticals();
        } else {
          toast.warning('Import completed with some issues');
        }
      } else {
        // Bulk import mode (shouldn't happen as we removed bulk import from list view)
        const res = isDocx ? await bulkImportVerticalsDocx(file) : await bulkImportVerticals(file);
        setImportResult(res.data);
        if (res.data.success) {
          toast.success(`Import successful! Created: ${res.data.created}, Updated: ${res.data.updated}`);
          fetchVerticals();
        } else {
          toast.warning(`Import completed with errors. Created: ${res.data.created}, Updated: ${res.data.updated}`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
      setImportResult({ success: false, message: error.response?.data?.detail || 'Import failed', errors: [error.message] });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSelectVertical = (vertical) => {
    setSelectedVertical(vertical);
    setFormData({
      name: vertical.name || "",
      description: vertical.description || "",
      icon: vertical.icon || "Building2",
      image_url: vertical.image_url || "",
      order: vertical.order || 0,
      testimonials: vertical.testimonials || [],
      investment_range: vertical.investment_range || "",
      total_duration: vertical.total_duration || "",
      before_after: vertical.before_after || [],
      deliverables: vertical.deliverables || [],
      roi_calculator: vertical.roi_calculator || { enabled: false, metrics: [] },
      faq: vertical.faq || [],
      brochures: vertical.brochures || [],
      videos: vertical.videos || [],
      competitor_comparison: vertical.competitor_comparison || { enabled: false, competitors: [], features: [] },
      implementation_timeline: vertical.implementation_timeline || { enabled: false, phases: [] },
      value_delivered: vertical.value_delivered || {}
    });
    setIsEditing(true);
    setActiveTab("basic");
  };

  const handleSave = async () => {
    if (!formData.name || !formData.description) {
      toast.error("Please fill in name and description");
      return;
    }
    setSaving(true);
    try {
      const cleanedData = {
        ...formData,
        testimonials: formData.testimonials.filter(t => t.name && t.quote),
        before_after: formData.before_after.filter(ba => ba.aspect),
        deliverables: formData.deliverables.filter(d => d.title),
        faq: formData.faq.filter(f => f.question && f.answer),
        brochures: formData.brochures.filter(b => b.title && b.file_url),
        videos: formData.videos.filter(v => v.youtube_url),
        value_delivered: Object.fromEntries(
          Object.entries(formData.value_delivered || {}).filter(([_, v]) => v)
        )
      };
      
      await updateVertical(selectedVertical.id, cleanedData);
      toast.success("Vertical updated successfully");
      fetchVerticals();
      // Update selected vertical with new data
      setSelectedVertical({ ...selectedVertical, ...cleanedData });
    } catch (error) {
      toast.error("Failed to save vertical");
    } finally {
      setSaving(false);
    }
  };

  const handleAddVertical = async () => {
    if (!newVerticalData.name || !newVerticalData.description) {
      toast.error("Please fill in name and description");
      return;
    }
    setSaving(true);
    try {
      const res = await createVertical({
        ...newVerticalData,
        testimonials: [], before_after: [], deliverables: [], faq: [], brochures: [], videos: [],
        roi_calculator: { enabled: false, metrics: [] },
        competitor_comparison: { enabled: false, competitors: [], features: [] },
        implementation_timeline: { enabled: false, phases: [] }
      });
      toast.success("Vertical created successfully");
      setAddDialogOpen(false);
      setNewVerticalData({ name: "", description: "", icon: "Building2", image_url: "", order: 0 });
      fetchVerticals();
      // Select the newly created vertical
      if (res.data?.id) {
        handleSelectVertical(res.data);
      }
    } catch (error) {
      toast.error("Failed to create vertical");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this vertical and all its problems?")) return;
    try {
      await deleteVertical(id);
      toast.success("Vertical deleted");
      if (selectedVertical?.id === id) {
        setSelectedVertical(null);
        setIsEditing(false);
        setFormData(null);
      }
      fetchVerticals();
    } catch (error) {
      toast.error("Failed to delete vertical");
    }
  };

  const handleBackToList = () => {
    setSelectedVertical(null);
    setIsEditing(false);
    setFormData(null);
  };

  // Video management with transcript extraction
  const addVideo = () => {
    setFormData({ ...formData, videos: [...(formData.videos || []), { ...emptyVideo, _key: uid() }] });
  };

  const updateVideo = async (index, key, value) => {
    const videos = [...(formData.videos || [])];
    videos[index] = { ...videos[index], [key]: value };
    setFormData({ ...formData, videos });

    // If URL was updated and is valid, extract transcript
    if (key === 'youtube_url' && value && getYouTubeVideoId(value)) {
      setProcessingVideo(index);
      try {
        const res = await extractYouTubeTranscript({ youtube_url: value, target_kb: 'ai_consultant' });
        if (res.data?.success) {
          videos[index].transcript_dataset_id = res.data.video_id;
          setFormData({ ...formData, videos });
          toast.success(`Transcript extracted for "${res.data.title}"`);
        }
      } catch (error) {
        toast.error("Failed to extract transcript");
      } finally {
        setProcessingVideo(null);
      }
    }
  };

  const removeVideo = async (index) => {
    const videos = [...(formData.videos || [])];
    const video = videos[index];
    
    // If video had transcript, we should note it (deletion handled by backend)
    if (video.transcript_dataset_id) {
      toast.info("Transcript dataset will be removed");
    }
    
    videos.splice(index, 1);
    setFormData({ ...formData, videos });
  };

  const openVideoModal = (url) => {
    setPlayingVideo(url);
    setVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setPlayingVideo(null);
    setVideoModalOpen(false);
  };

  // Helper functions for array management
  const addItem = (field, empty) => setFormData({ ...formData, [field]: [...formData[field], { ...empty }] });
  const removeItem = (field, index) => setFormData({ ...formData, [field]: formData[field].filter((_, i) => i !== index) });
  const updateItem = (field, index, key, value) => {
    const items = [...formData[field]];
    items[index] = { ...items[index], [key]: value };
    setFormData({ ...formData, [field]: items });
  };

  // ROI metrics
  const addROIMetric = () => {
    setFormData({
      ...formData,
      roi_calculator: {
        ...formData.roi_calculator,
        metrics: [...(formData.roi_calculator.metrics || []), { label: "", current_value: "", improved_value: "", unit: "%", _key: uid() }]
      }
    });
  };
  const updateROIMetric = (index, key, value) => {
    const metrics = [...(formData.roi_calculator.metrics || [])];
    metrics[index] = { ...metrics[index], [key]: value };
    setFormData({ ...formData, roi_calculator: { ...formData.roi_calculator, metrics } });
  };
  const removeROIMetric = (index) => {
    setFormData({
      ...formData,
      roi_calculator: {
        ...formData.roi_calculator,
        metrics: formData.roi_calculator.metrics.filter((_, i) => i !== index)
      }
    });
  };

  // Competitor comparison
  const addCompetitor = () => {
    setFormData({
      ...formData,
      competitor_comparison: {
        ...formData.competitor_comparison,
        competitors: [...(formData.competitor_comparison.competitors || []), { name: "", logo_url: "", _key: uid() }]
      }
    });
  };
  const updateCompetitor = (index, key, value) => {
    const competitors = [...(formData.competitor_comparison.competitors || [])];
    competitors[index] = { ...competitors[index], [key]: value };
    setFormData({ ...formData, competitor_comparison: { ...formData.competitor_comparison, competitors } });
  };
  const removeCompetitor = (index) => {
    setFormData({
      ...formData,
      competitor_comparison: {
        ...formData.competitor_comparison,
        competitors: formData.competitor_comparison.competitors.filter((_, i) => i !== index)
      }
    });
  };
  const addFeature = () => {
    const newFeature = { _key: uid(), name: "", absolin: "yes", values: {} };
    formData.competitor_comparison.competitors?.forEach((_, i) => {
      newFeature.values[`comp_${i}`] = "no";
    });
    setFormData({
      ...formData,
      competitor_comparison: {
        ...formData.competitor_comparison,
        features: [...(formData.competitor_comparison.features || []), newFeature]
      }
    });
  };
  const updateFeature = (index, key, value) => {
    const features = [...(formData.competitor_comparison.features || [])];
    features[index] = { ...features[index], [key]: value };
    setFormData({ ...formData, competitor_comparison: { ...formData.competitor_comparison, features } });
  };
  const updateFeatureValue = (fIndex, compKey, value) => {
    const features = [...(formData.competitor_comparison.features || [])];
    features[fIndex] = { ...features[fIndex], values: { ...features[fIndex].values, [compKey]: value } };
    setFormData({ ...formData, competitor_comparison: { ...formData.competitor_comparison, features } });
  };
  const removeFeature = (index) => {
    setFormData({
      ...formData,
      competitor_comparison: {
        ...formData.competitor_comparison,
        features: formData.competitor_comparison.features.filter((_, i) => i !== index)
      }
    });
  };

  // Implementation timeline
  const addPhase = () => {
    setFormData({
      ...formData,
      implementation_timeline: {
        ...formData.implementation_timeline,
        phases: [...(formData.implementation_timeline.phases || []), { _key: uid(), name: "", duration: "", tasks: [], milestones: [] }]
      }
    });
  };
  const updatePhase = (index, key, value) => {
    const phases = [...(formData.implementation_timeline.phases || [])];
    phases[index] = { ...phases[index], [key]: value };
    setFormData({ ...formData, implementation_timeline: { ...formData.implementation_timeline, phases } });
  };
  const removePhase = (index) => {
    setFormData({
      ...formData,
      implementation_timeline: {
        ...formData.implementation_timeline,
        phases: formData.implementation_timeline.phases.filter((_, i) => i !== index)
      }
    });
  };
  const addTask = (phaseIndex) => {
    const phases = [...(formData.implementation_timeline.phases || [])];
    phases[phaseIndex].tasks = [...(phases[phaseIndex].tasks || []), { _key: uid(), title: "", description: "" }];
    setFormData({ ...formData, implementation_timeline: { ...formData.implementation_timeline, phases } });
  };
  const updateTask = (phaseIndex, taskIndex, key, value) => {
    const phases = [...(formData.implementation_timeline.phases || [])];
    phases[phaseIndex].tasks[taskIndex] = { ...phases[phaseIndex].tasks[taskIndex], [key]: value };
    setFormData({ ...formData, implementation_timeline: { ...formData.implementation_timeline, phases } });
  };
  const removeTask = (phaseIndex, taskIndex) => {
    const phases = [...(formData.implementation_timeline.phases || [])];
    phases[phaseIndex].tasks = phases[phaseIndex].tasks.filter((_, i) => i !== taskIndex);
    setFormData({ ...formData, implementation_timeline: { ...formData.implementation_timeline, phases } });
  };
  const addMilestone = (phaseIndex) => {
    const phases = [...(formData.implementation_timeline.phases || [])];
    phases[phaseIndex].milestones = [...(phases[phaseIndex].milestones || []), ""];
    setFormData({ ...formData, implementation_timeline: { ...formData.implementation_timeline, phases } });
  };
  const updateMilestone = (phaseIndex, msIndex, value) => {
    const phases = [...(formData.implementation_timeline.phases || [])];
    phases[phaseIndex].milestones[msIndex] = value;
    setFormData({ ...formData, implementation_timeline: { ...formData.implementation_timeline, phases } });
  };
  const removeMilestone = (phaseIndex, msIndex) => {
    const phases = [...(formData.implementation_timeline.phases || [])];
    phases[phaseIndex].milestones = phases[phaseIndex].milestones.filter((_, i) => i !== msIndex);
    setFormData({ ...formData, implementation_timeline: { ...formData.implementation_timeline, phases } });
  };

  // MAIN RENDER
  if (isEditing && formData) {
    return (
      <AdminLayout title={`Edit: ${formData.name}`}>
        <div className="space-y-4" data-testid="admin-vertical-editor">
          {/* Header with Back, Import, and Save */}
          <div className="flex items-center justify-between bg-white rounded-lg p-4 shadow-sm border">
            <Button variant="ghost" onClick={handleBackToList} className="gap-2">
              <ChevronLeft className="h-4 w-4" /> Back to List
            </Button>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-500 hidden md:inline">Editing: <strong>{formData.name}</strong></span>
              
              {/* Import Data Button */}
              <div className="relative">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".docx,.json"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="vertical-import-file"
                  data-testid="vertical-import-file-input"
                />
                <Button 
                  variant="outline" 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="gap-2"
                  data-testid="vertical-import-btn"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  Import Data
                </Button>
              </div>
              
              <Button onClick={handleSave} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3] gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Changes
              </Button>
            </div>
          </div>
          
          {/* Import Result Toast (shown below header when import completes) */}
          {importResult && (
            <div className={`p-4 rounded-lg border ${importResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-start gap-2">
                {importResult.success ? (
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`font-medium ${importResult.success ? 'text-emerald-800' : 'text-amber-800'}`}>
                    {importResult.message}
                  </p>
                  {importResult.errors?.length > 0 && (
                    <div className="mt-2 text-sm text-red-600">
                      <ul className="list-disc list-inside">
                        {importResult.errors.slice(0, 3).map((err, i) => (
                          <li key={`imp-err-${i}-${err.slice(0, 20)}`}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setImportResult(null)} className="h-6 w-6 p-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Main Content Area - Sidebar + Content */}
          <div className="grid grid-cols-12 gap-4">
            {/* Sidebar Navigation */}
            <div className="col-span-12 md:col-span-3 lg:col-span-2">
              <Card className="sticky top-4">
                <CardContent className="p-2">
                  <nav className="space-y-1">
                    {[
                      { id: "basic", icon: Building, label: "Basic Info" },
                      { id: "overview", icon: TrendingUp, label: "Overview" },
                      { id: "pricing", icon: IndianRupee, label: "Pricing" },
                      { id: "videos", icon: Video, label: "Videos" },
                      { id: "testimonials", icon: Quote, label: "Testimonials" },
                      { id: "beforeafter", icon: ArrowLeftRight, label: "Before/After" },
                      { id: "deliverables", icon: Package, label: "Deliverables" },
                      { id: "roi", icon: Calculator, label: "ROI Calculator" },
                      { id: "faq", icon: HelpCircle, label: "FAQ" },
                      { id: "brochures", icon: FileText, label: "Brochures" },
                      { id: "competitors", icon: GitCompare, label: "Competitors" },
                      { id: "timeline", icon: Milestone, label: "Timeline" },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          activeTab === tab.id 
                            ? 'bg-[#0066CC] text-white' 
                            : 'hover:bg-slate-100 text-slate-700'
                        }`}
                      >
                        <tab.icon className="h-4 w-4" />
                        <span className="truncate">{tab.label}</span>
                      </button>
                    ))}
                  </nav>
                </CardContent>
              </Card>
            </div>

            {/* Content Area */}
            <div className="col-span-12 md:col-span-9 lg:col-span-10">
              <Card>
                <CardContent className="p-6">
                  {/* Basic Tab */}
                  {activeTab === "basic" && (
                    <div className="space-y-6">
                      <h3 className="text-lg font-semibold border-b pb-2">Basic Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label>Name *</Label>
                          <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Retail & Distribution" />
                        </div>
                        <div className="space-y-2">
                          <Label>Display Order</Label>
                          <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })} />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Description *</Label>
                        <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={4} className="resize-none" />
                      </div>
                      <div className="space-y-2">
                        <Label>Icon</Label>
                        <div className="flex flex-wrap gap-2">
                          {iconOptions.map((icon) => (
                            <button key={icon} type="button" onClick={() => setFormData({ ...formData, icon })} className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${formData.icon === icon ? 'bg-[#0066CC] text-white ring-2 ring-[#0066CC] ring-offset-2' : 'bg-slate-100 hover:bg-slate-200'}`}>
                              {getIcon(icon, "h-6 w-6")}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Hero Image URL</Label>
                        <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="https://..." />
                        {formData.image_url && (
                          <div className="mt-2 rounded-lg overflow-hidden border max-h-40">
                            <img src={formData.image_url} alt="Preview" className="w-full h-40 object-cover" onError={(e) => e.target.style.display = 'none'} />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Overview Tab - Value Delivered */}
                  {activeTab === "overview" && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center border-b pb-2">
                        <div>
                          <h3 className="text-lg font-semibold">Value Delivered</h3>
                          <p className="text-sm text-slate-500">Measurable business outcomes shown on the vertical page</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries({
                          time_saving: "Time Saving",
                          cost_reduction: "Cost Reduction",
                          efficiency_gain: "Efficiency Gain",
                          error_reduction: "Error Reduction",
                          roi: "ROI",
                          roi_period: "ROI Timeline",
                          annual_savings: "Annual Savings",
                          inventory: "Inventory Optimization",
                          reporting: "Reporting & Analytics",
                          compliance: "Compliance"
                        }).map(([key, label]) => (
                          <div key={key} className="space-y-2 p-3 bg-slate-50 rounded-lg">
                            <Label className="text-sm font-medium">{label}</Label>
                            <Input
                              value={formData.value_delivered?.[key] || ""}
                              onChange={(e) => setFormData({
                                ...formData,
                                value_delivered: { ...formData.value_delivered, [key]: e.target.value }
                              })}
                              placeholder={`e.g., ${key === 'roi' ? '300% ROI' : key === 'cost_reduction' ? '15-25% reduction' : '40% improvement'}`}
                              data-testid={`value-delivered-${key}`}
                            />
                          </div>
                        ))}
                      </div>

                      {/* Custom Key-Value Entries */}
                      <div className="border-t pt-4">
                        <div className="flex justify-between items-center mb-3">
                          <div>
                            <Label className="text-sm font-semibold">Custom Metrics</Label>
                            <p className="text-xs text-slate-500 mt-0.5">Add additional value metrics beyond the standard ones above</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const key = `custom_${Date.now()}`;
                              setFormData({
                                ...formData,
                                value_delivered: { ...formData.value_delivered, [key]: "" }
                              });
                            }}
                            className="gap-2"
                            data-testid="add-custom-metric-btn"
                          >
                            <Plus className="h-4 w-4" /> Add Custom Metric
                          </Button>
                        </div>
                        {Object.entries(formData.value_delivered || {})
                          .filter(([key]) => !["time_saving", "cost_reduction", "efficiency_gain", "error_reduction", "roi", "roi_period", "annual_savings", "inventory", "reporting", "compliance"].includes(key))
                          .map(([key, value]) => (
                            <div key={key} className="flex items-center gap-3 mb-3">
                              <Input
                                value={key}
                                onChange={(e) => {
                                  const newVd = { ...formData.value_delivered };
                                  const val = newVd[key];
                                  delete newVd[key];
                                  newVd[e.target.value] = val;
                                  setFormData({ ...formData, value_delivered: newVd });
                                }}
                                placeholder="Metric key"
                                className="w-48"
                              />
                              <Input
                                value={value}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  value_delivered: { ...formData.value_delivered, [key]: e.target.value }
                                })}
                                placeholder="Metric value"
                                className="flex-1"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newVd = { ...formData.value_delivered };
                                  delete newVd[key];
                                  setFormData({ ...formData, value_delivered: newVd });
                                }}
                                className="text-red-500"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Pricing Tab */}
                  {activeTab === "pricing" && (
                    <div className="space-y-6">
                      <h3 className="text-lg font-semibold border-b pb-2">Investment & Timeline</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2 p-4 bg-emerald-50 rounded-lg">
                          <Label className="flex items-center gap-2"><IndianRupee className="h-4 w-4 text-emerald-600" /> Investment Range</Label>
                          <Input value={formData.investment_range} onChange={(e) => setFormData({ ...formData, investment_range: e.target.value })} placeholder="e.g., ₹2-5 Lakhs" className="text-lg font-semibold" />
                        </div>
                        <div className="space-y-2 p-4 bg-blue-50 rounded-lg">
                          <Label className="flex items-center gap-2"><Clock className="h-4 w-4 text-blue-600" /> Total Duration</Label>
                          <Input value={formData.total_duration} onChange={(e) => setFormData({ ...formData, total_duration: e.target.value })} placeholder="e.g., 4-8 Weeks" className="text-lg font-semibold" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Videos Tab - NEW */}
                  {activeTab === "videos" && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center border-b pb-2">
                        <div>
                          <h3 className="text-lg font-semibold">Solution Videos</h3>
                          <p className="text-sm text-slate-500">Add YouTube videos - transcripts are auto-extracted for AI training</p>
                        </div>
                        <Button onClick={addVideo} variant="outline" className="gap-2">
                          <Plus className="h-4 w-4" /> Add Video
                        </Button>
                      </div>
                      
                      {(!formData.videos || formData.videos.length === 0) ? (
                        <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed">
                          <Video className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                          <p className="text-slate-500">No videos added yet</p>
                          <Button onClick={addVideo} variant="outline" className="mt-4">Add Your First Video</Button>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {formData.videos.map((video, i) => (
                            <Card key={video._key || `video-${i}`} className="overflow-hidden">
                              {video.youtube_url && getYouTubeVideoId(video.youtube_url) ? (
                                <div className="relative aspect-video bg-slate-900 cursor-pointer group" onClick={() => openVideoModal(video.youtube_url)}>
                                  <img 
                                    src={`https://img.youtube.com/vi/${getYouTubeVideoId(video.youtube_url)}/hqdefault.jpg`}
                                    alt={video.title || "Video thumbnail"}
                                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                      <Play className="h-8 w-8 text-white ml-1" />
                                    </div>
                                  </div>
                                  {processingVideo === i && (
                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                      <div className="text-white text-center">
                                        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                                        <p className="text-sm">Extracting transcript...</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="aspect-video bg-slate-100 flex items-center justify-center">
                                  <Video className="h-12 w-12 text-slate-300" />
                                </div>
                              )}
                              <CardContent className="p-4 space-y-3">
                                <Input
                                  value={video.title}
                                  onChange={(e) => updateVideo(i, 'title', e.target.value)}
                                  placeholder="Video Title"
                                  className="font-medium"
                                />
                                <Input
                                  value={video.youtube_url}
                                  onChange={(e) => updateVideo(i, 'youtube_url', e.target.value)}
                                  placeholder="YouTube URL"
                                />
                                <div className="flex justify-between items-center">
                                  {video.transcript_dataset_id && (
                                    <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded">
                                      ✓ Transcript extracted
                                    </span>
                                  )}
                                  <Button variant="ghost" size="sm" onClick={() => removeVideo(i)} className="text-red-500 ml-auto">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Testimonials Tab */}
                  {activeTab === "testimonials" && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center border-b pb-2">
                        <h3 className="text-lg font-semibold">Client Testimonials</h3>
                        <Button onClick={() => addItem('testimonials', emptyTestimonial)} variant="outline" className="gap-2">
                          <Plus className="h-4 w-4" /> Add Testimonial
                        </Button>
                      </div>
                      
                      {formData.testimonials.length === 0 ? (
                        <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed">
                          <Quote className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                          <p className="text-slate-500">No testimonials added yet</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {formData.testimonials.map((t, i) => (
                            <Card key={t._key || `testimonial-${i}`} className="p-4">
                              <div className="flex justify-between items-start mb-4">
                                <span className="font-medium text-[#0066CC]">Testimonial {i + 1}</span>
                                <Button variant="ghost" size="sm" onClick={() => removeItem('testimonials', i)} className="text-red-500 h-8 w-8 p-0">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <Input value={t.name} onChange={(e) => updateItem('testimonials', i, 'name', e.target.value)} placeholder="Client Name" />
                                <Input value={t.role} onChange={(e) => updateItem('testimonials', i, 'role', e.target.value)} placeholder="Role/Position" />
                                <Input value={t.company} onChange={(e) => updateItem('testimonials', i, 'company', e.target.value)} placeholder="Company Name" />
                              </div>
                              <Textarea value={t.quote} onChange={(e) => updateItem('testimonials', i, 'quote', e.target.value)} placeholder="Client's testimonial quote..." rows={3} className="mb-4" />
                              <Input value={t.image_url} onChange={(e) => updateItem('testimonials', i, 'image_url', e.target.value)} placeholder="Profile Image URL (optional)" />
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Before/After Tab */}
                  {activeTab === "beforeafter" && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center border-b pb-2">
                        <h3 className="text-lg font-semibold">Before vs After Comparisons</h3>
                        <Button onClick={() => addItem('before_after', emptyBeforeAfter)} variant="outline" className="gap-2">
                          <Plus className="h-4 w-4" /> Add Comparison
                        </Button>
                      </div>
                      
                      {formData.before_after.length === 0 ? (
                        <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed">
                          <ArrowLeftRight className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                          <p className="text-slate-500">No comparisons added yet</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {formData.before_after.map((ba, i) => (
                            <Card key={ba._key || `ba-${i}`} className="p-4">
                              <div className="flex justify-between items-center mb-4">
                                <Input value={ba.aspect} onChange={(e) => updateItem('before_after', i, 'aspect', e.target.value)} placeholder="Aspect (e.g., Inventory Accuracy)" className="max-w-md font-medium" />
                                <Button variant="ghost" size="sm" onClick={() => removeItem('before_after', i)} className="text-red-500">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="p-4 bg-red-50 rounded-lg">
                                  <Label className="text-red-600 font-medium mb-2 block">Before ABSOLIN</Label>
                                  <Textarea value={ba.before} onChange={(e) => updateItem('before_after', i, 'before', e.target.value)} rows={3} placeholder="Describe the situation before..." />
                                </div>
                                <div className="p-4 bg-green-50 rounded-lg">
                                  <Label className="text-green-600 font-medium mb-2 block">After ABSOLIN</Label>
                                  <Textarea value={ba.after} onChange={(e) => updateItem('before_after', i, 'after', e.target.value)} rows={3} placeholder="Describe the improvement after..." />
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Deliverables Tab */}
                  {activeTab === "deliverables" && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center border-b pb-2">
                        <h3 className="text-lg font-semibold">What Clients Get</h3>
                        <Button onClick={() => addItem('deliverables', emptyDeliverable)} variant="outline" className="gap-2">
                          <Plus className="h-4 w-4" /> Add Deliverable
                        </Button>
                      </div>
                      
                      {formData.deliverables.length === 0 ? (
                        <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed">
                          <Package className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                          <p className="text-slate-500">No deliverables added yet</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {formData.deliverables.map((d, i) => (
                            <Card key={d._key || `del-${i}`} className="p-4">
                              <div className="flex justify-between items-start mb-3">
                                <div className="flex items-center gap-3">
                                  <Select value={d.icon || "CheckCircle"} onValueChange={(v) => updateItem('deliverables', i, 'icon', v)}>
                                    <SelectTrigger className="w-24">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {deliverableIcons.map(icon => (
                                        <SelectItem key={icon} value={icon}>
                                          <span className="flex items-center gap-2">{getIcon(icon, "h-4 w-4")}</span>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => removeItem('deliverables', i)} className="text-red-500">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                              <Input value={d.title} onChange={(e) => updateItem('deliverables', i, 'title', e.target.value)} placeholder="Deliverable Title" className="mb-3 font-medium" />
                              <Textarea value={d.description} onChange={(e) => updateItem('deliverables', i, 'description', e.target.value)} placeholder="Description..." rows={2} />
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ROI Tab */}
                  {activeTab === "roi" && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center border-b pb-2">
                        <div>
                          <h3 className="text-lg font-semibold">ROI Calculator</h3>
                          <p className="text-sm text-slate-500">Help prospects calculate their potential savings</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Label>Enable</Label>
                          <Switch checked={formData.roi_calculator.enabled} onCheckedChange={(checked) => setFormData({ ...formData, roi_calculator: { ...formData.roi_calculator, enabled: checked } })} />
                        </div>
                      </div>
                      
                      {formData.roi_calculator.enabled && (
                        <div className="space-y-4">
                          <Button onClick={addROIMetric} variant="outline" className="gap-2">
                            <Plus className="h-4 w-4" /> Add Metric
                          </Button>
                          {(formData.roi_calculator.metrics || []).map((m, i) => (
                            <Card key={m._key || `roi-${i}`} className="p-4">
                              <div className="flex justify-between items-center mb-3">
                                <span className="font-medium">Metric {i + 1}</span>
                                <Button variant="ghost" size="sm" onClick={() => removeROIMetric(i)} className="text-red-500">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                <div className="md:col-span-2">
                                  <Input value={m.label} onChange={(e) => updateROIMetric(i, 'label', e.target.value)} placeholder="Metric Label" />
                                </div>
                                <Input value={m.current_value} onChange={(e) => updateROIMetric(i, 'current_value', e.target.value)} placeholder="Current Value" />
                                <Input value={m.improved_value} onChange={(e) => updateROIMetric(i, 'improved_value', e.target.value)} placeholder="Improved Value" />
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* FAQ Tab */}
                  {activeTab === "faq" && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center border-b pb-2">
                        <h3 className="text-lg font-semibold">Frequently Asked Questions</h3>
                        <Button onClick={() => addItem('faq', emptyFAQ)} variant="outline" className="gap-2">
                          <Plus className="h-4 w-4" /> Add FAQ
                        </Button>
                      </div>
                      
                      {formData.faq.length === 0 ? (
                        <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed">
                          <HelpCircle className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                          <p className="text-slate-500">No FAQs added yet</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {formData.faq.map((f, i) => (
                            <Card key={f._key || `faq-${i}`} className="p-4">
                              <div className="flex justify-between items-start mb-3">
                                <span className="font-medium text-[#0066CC]">FAQ {i + 1}</span>
                                <Button variant="ghost" size="sm" onClick={() => removeItem('faq', i)} className="text-red-500">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                              <Input value={f.question} onChange={(e) => updateItem('faq', i, 'question', e.target.value)} placeholder="Question" className="mb-3 font-medium" />
                              <Textarea value={f.answer} onChange={(e) => updateItem('faq', i, 'answer', e.target.value)} placeholder="Answer" rows={3} />
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Brochures Tab */}
                  {activeTab === "brochures" && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center border-b pb-2">
                        <div>
                          <h3 className="text-lg font-semibold">Downloadable Brochures</h3>
                          <p className="text-sm text-slate-500">Users will provide contact info to download</p>
                        </div>
                        <Button onClick={() => addItem('brochures', emptyBrochure)} variant="outline" className="gap-2">
                          <Plus className="h-4 w-4" /> Add Brochure
                        </Button>
                      </div>
                      
                      {formData.brochures.length === 0 ? (
                        <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed">
                          <FileText className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                          <p className="text-slate-500">No brochures added yet</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {formData.brochures.map((b, i) => (
                            <Card key={b._key || `broch-${i}`} className="p-4">
                              <div className="flex justify-between items-start mb-3">
                                <span className="font-medium text-[#0066CC]">Brochure {i + 1}</span>
                                <Button variant="ghost" size="sm" onClick={() => removeItem('brochures', i)} className="text-red-500">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="space-y-3">
                                <Input value={b.title} onChange={(e) => updateItem('brochures', i, 'title', e.target.value)} placeholder="Brochure Title" />
                                <Input value={b.file_url} onChange={(e) => updateItem('brochures', i, 'file_url', e.target.value)} placeholder="PDF URL" />
                                <Textarea value={b.description} onChange={(e) => updateItem('brochures', i, 'description', e.target.value)} placeholder="Description" rows={2} />
                                <Input value={b.thumbnail_url} onChange={(e) => updateItem('brochures', i, 'thumbnail_url', e.target.value)} placeholder="Thumbnail URL (optional)" />
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Competitors Tab */}
                  {activeTab === "competitors" && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center border-b pb-2">
                        <div>
                          <h3 className="text-lg font-semibold">Competitor Comparison Matrix</h3>
                          <p className="text-sm text-slate-500">Why choose ABSOLIN over competitors</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Label>Enable</Label>
                          <Switch checked={formData.competitor_comparison.enabled} onCheckedChange={(checked) => setFormData({ ...formData, competitor_comparison: { ...formData.competitor_comparison, enabled: checked } })} />
                        </div>
                      </div>
                      
                      {formData.competitor_comparison.enabled && (
                        <div className="space-y-6">
                          {/* Competitors */}
                          <Card className="p-4">
                            <div className="flex justify-between items-center mb-4">
                              <Label className="text-base font-semibold">Competitors</Label>
                              <Button onClick={addCompetitor} variant="outline" size="sm" className="gap-2">
                                <Plus className="h-4 w-4" /> Add Competitor
                              </Button>
                            </div>
                            <div className="space-y-3">
                              {(formData.competitor_comparison.competitors || []).map((c, i) => (
                                <div key={c._key || `comp-${i}`} className="flex gap-3 items-center">
                                  <Input value={c.name} onChange={(e) => updateCompetitor(i, 'name', e.target.value)} placeholder={`Competitor ${i + 1} Name`} className="flex-1" />
                                  <Input value={c.logo_url} onChange={(e) => updateCompetitor(i, 'logo_url', e.target.value)} placeholder="Logo URL" className="flex-1" />
                                  <Button variant="ghost" size="sm" onClick={() => removeCompetitor(i)} className="text-red-500">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </Card>

                          {/* Features */}
                          <Card className="p-4">
                            <div className="flex justify-between items-center mb-4">
                              <Label className="text-base font-semibold">Features to Compare</Label>
                              <Button onClick={addFeature} variant="outline" size="sm" className="gap-2">
                                <Plus className="h-4 w-4" /> Add Feature
                              </Button>
                            </div>
                            <div className="space-y-4">
                              {(formData.competitor_comparison.features || []).map((f, fi) => (
                                <Card key={f._key || `feature-${fi}`} className="p-4 bg-slate-50">
                                  <div className="flex justify-between items-center mb-3">
                                    <Input value={f.name} onChange={(e) => updateFeature(fi, 'name', e.target.value)} placeholder="Feature name" className="max-w-sm" />
                                    <Button variant="ghost" size="sm" onClick={() => removeFeature(fi)} className="text-red-500">
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                  <div className="flex gap-4 flex-wrap">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-[#0066CC]">ABSOLIN:</span>
                                      <Select value={f.absolin} onValueChange={(v) => updateFeature(fi, 'absolin', v)}>
                                        <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="yes">✓ Yes</SelectItem>
                                          <SelectItem value="no">✗ No</SelectItem>
                                          <SelectItem value="partial">◐ Partial</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    {(formData.competitor_comparison.competitors || []).map((c, ci) => (
                                      <div key={c._key || `comp-${ci}`} className="flex items-center gap-2">
                                        <span className="text-sm">{c.name || `Comp ${ci + 1}`}:</span>
                                        <Select value={f.values?.[`comp_${ci}`] || "no"} onValueChange={(v) => updateFeatureValue(fi, `comp_${ci}`, v)}>
                                          <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="yes">✓ Yes</SelectItem>
                                            <SelectItem value="no">✗ No</SelectItem>
                                            <SelectItem value="partial">◐ Partial</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    ))}
                                  </div>
                                </Card>
                              ))}
                            </div>
                          </Card>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Timeline Tab */}
                  {activeTab === "timeline" && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center border-b pb-2">
                        <div>
                          <h3 className="text-lg font-semibold">Implementation Timeline</h3>
                          <p className="text-sm text-slate-500">Project phases and milestones</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Label>Enable</Label>
                          <Switch checked={formData.implementation_timeline.enabled} onCheckedChange={(checked) => setFormData({ ...formData, implementation_timeline: { ...formData.implementation_timeline, enabled: checked } })} />
                        </div>
                      </div>
                      
                      {formData.implementation_timeline.enabled && (
                        <div className="space-y-4">
                          <Button onClick={addPhase} variant="outline" className="gap-2">
                            <Plus className="h-4 w-4" /> Add Phase
                          </Button>
                          {(formData.implementation_timeline.phases || []).map((phase, pi) => (
                            <Card key={phase._key || `phase-${pi}`} className="p-4">
                              <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center gap-4">
                                  <div className="w-10 h-10 rounded-full bg-[#0066CC] text-white flex items-center justify-center font-bold text-lg">{pi + 1}</div>
                                  <div className="flex gap-3">
                                    <Input value={phase.name} onChange={(e) => updatePhase(pi, 'name', e.target.value)} placeholder="Phase Name" className="w-48" />
                                    <Input value={phase.duration} onChange={(e) => updatePhase(pi, 'duration', e.target.value)} placeholder="Duration" className="w-32" />
                                  </div>
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => removePhase(pi)} className="text-red-500">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                              
                              {/* Tasks */}
                              <div className="ml-14 mb-4">
                                <div className="flex justify-between items-center mb-2">
                                  <Label className="text-sm font-medium">Tasks</Label>
                                  <Button variant="ghost" size="sm" onClick={() => addTask(pi)} className="gap-1 h-7">
                                    <Plus className="h-3 w-3" /> Add Task
                                  </Button>
                                </div>
                                <div className="space-y-2">
                                  {(phase.tasks || []).map((task, ti) => (
                                    <div key={task._key || `task-${pi}-${ti}`} className="flex gap-2 items-center">
                                      <Input value={task.title} onChange={(e) => updateTask(pi, ti, 'title', e.target.value)} placeholder="Task title" className="flex-1" />
                                      <Input value={task.description} onChange={(e) => updateTask(pi, ti, 'description', e.target.value)} placeholder="Description" className="flex-1" />
                                      <Button variant="ghost" size="sm" onClick={() => removeTask(pi, ti)} className="text-red-500 h-9 w-9 p-0">
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              
                              {/* Milestones */}
                              <div className="ml-14">
                                <div className="flex justify-between items-center mb-2">
                                  <Label className="text-sm font-medium">Milestones</Label>
                                  <Button variant="ghost" size="sm" onClick={() => addMilestone(pi)} className="gap-1 h-7">
                                    <Plus className="h-3 w-3" /> Add
                                  </Button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {(phase.milestones || []).map((ms, mi) => (
                                    <div key={mi} className="flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
                                      <Input value={ms} onChange={(e) => updateMilestone(pi, mi, e.target.value)} placeholder="Milestone" className="h-6 w-32 bg-transparent border-0 p-0 text-sm focus-visible:ring-0" />
                                      <Button variant="ghost" size="sm" onClick={() => removeMilestone(pi, mi)} className="h-5 w-5 p-0 text-red-500 hover:bg-red-50 rounded-full">
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Video Modal */}
        <Dialog open={videoModalOpen} onOpenChange={closeVideoModal}>
          <DialogContent className="max-w-4xl p-0 overflow-hidden">
            <DialogHeader className="p-4 pb-0">
              <div className="flex justify-between items-center">
                <DialogTitle>Video Player</DialogTitle>
                <Button variant="ghost" size="sm" onClick={closeVideoModal} className="h-8 w-8 p-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </DialogHeader>
            <div className="aspect-video">
              {playingVideo && (
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${getYouTubeVideoId(playingVideo)}?autoplay=1`}
                  title="Video Player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </AdminLayout>
    );
  }

  // LIST VIEW
  return (
    <AdminLayout title="Manage Verticals">
      <div className="space-y-6" data-testid="admin-verticals">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <p className="text-slate-600">Manage verticals with testimonials, videos, pricing, and more</p>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setBulkImportDialogOpen(true)} data-testid="download-template-dialog-btn">
              <FileText className="h-4 w-4 mr-2" /> Download Template
            </Button>
            <Button variant="outline" onClick={handleExportVerticals} data-testid="export-btn">
              <Download className="h-4 w-4 mr-2" /> Export All (JSON)
            </Button>
            <Button variant="outline" onClick={handleDownloadPlaybook} disabled={downloadingPlaybook} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="market-pitch-btn">
              {downloadingPlaybook ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />} Market Pitch Playbook (.docx)
            </Button>
            <Button variant="outline" onClick={() => { setEnvSyncOpen(true); setEnvSyncResult(null); setEnvSyncFile(null); }} className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="env-sync-btn">
              <Share2 className="h-4 w-4 mr-2" /> Environment Sync
            </Button>
            <Button onClick={() => setAddDialogOpen(true)} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="add-vertical-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Vertical
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card><CardContent className="p-4"><p className="text-sm text-slate-500">Total Verticals</p><p className="text-2xl font-bold">{verticals.length}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-sm text-slate-500">With Videos</p><p className="text-2xl font-bold text-red-600">{verticals.filter(v => v.videos?.length > 0).length}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-sm text-slate-500">With Testimonials</p><p className="text-2xl font-bold text-purple-600">{verticals.filter(v => v.testimonials?.length > 0).length}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-sm text-slate-500">With Brochures</p><p className="text-2xl font-bold text-blue-600">{verticals.filter(v => v.brochures?.length > 0).length}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-sm text-slate-500">With Timeline</p><p className="text-2xl font-bold text-emerald-600">{verticals.filter(v => v.implementation_timeline?.enabled).length}</p></CardContent></Card>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Icon</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden md:table-cell">Problems</TableHead>
                  <TableHead className="w-32">Features</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></TableCell></TableRow>
                ) : verticals.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">No verticals found</TableCell></TableRow>
                ) : (
                  verticals.map((v) => (
                    <TableRow key={v.id} className="cursor-pointer hover:bg-slate-50" onClick={() => handleSelectVertical(v)}>
                      <TableCell><div className="w-10 h-10 rounded-lg bg-[#E6F0FA] flex items-center justify-center text-[#0066CC]">{getIcon(v.icon, "h-5 w-5")}</div></TableCell>
                      <TableCell className="font-medium">{v.name}</TableCell>
                      <TableCell className="hidden md:table-cell"><span className="px-2 py-1 rounded-full bg-slate-100 text-sm">{v.problem_count || 0}</span></TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {v.videos?.length > 0 && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-xs">{v.videos.length} vid</span>}
                          {v.testimonials?.length > 0 && <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-xs">{v.testimonials.length} quotes</span>}
                          {v.brochures?.length > 0 && <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-xs">{v.brochures.length} docs</span>}
                          {v.implementation_timeline?.enabled && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-xs">timeline</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" onClick={() => handleSelectVertical(v)}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(v.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add Vertical Dialog */}
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Vertical</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={newVerticalData.name} onChange={(e) => setNewVerticalData({ ...newVerticalData, name: e.target.value })} placeholder="e.g., Retail & Distribution" />
              </div>
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea value={newVerticalData.description} onChange={(e) => setNewVerticalData({ ...newVerticalData, description: e.target.value })} rows={3} placeholder="Brief description..." />
              </div>
              <div className="space-y-2">
                <Label>Icon</Label>
                <div className="flex flex-wrap gap-2">
                  {iconOptions.slice(0, 10).map((icon) => (
                    <button key={icon} type="button" onClick={() => setNewVerticalData({ ...newVerticalData, icon })} className={`w-10 h-10 rounded-lg flex items-center justify-center ${newVerticalData.icon === icon ? 'bg-[#0066CC] text-white' : 'bg-slate-100 hover:bg-slate-200'}`}>
                      {getIcon(icon, "h-5 w-5")}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddVertical} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3]">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Vertical
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Download Template Dialog */}
        <Dialog open={bulkImportDialogOpen} onOpenChange={setBulkImportDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="download-template-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" /> Download Template
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Template Options */}
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Download a template to create or update verticals offline. Use the import option within each vertical&apos;s edit page to upload.</p>
                
                {/* Word Template - Primary */}
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-blue-800">MS Word Template</p>
                        <p className="text-xs text-blue-600">Recommended - Easy to fill tables</p>
                      </div>
                    </div>
                    <Button size="sm" onClick={handleDownloadWordTemplate} className="bg-blue-600 hover:bg-blue-700" data-testid="download-word-template-btn">
                      <Download className="h-4 w-4 mr-2" /> Download .docx
                    </Button>
                  </div>
                </div>
                
                {/* JSON Templates - Secondary */}
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleDownloadTemplate('vertical')} data-testid="download-template-btn" className="flex-1">
                    <FileText className="h-4 w-4 mr-2" /> JSON Template (Empty)
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDownloadTemplate('vertical_example')} data-testid="download-example-btn" className="flex-1">
                    <Eye className="h-4 w-4 mr-2" /> JSON Example (Pharma)
                  </Button>
                </div>
              </div>

              {/* Tips */}
              <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                <p className="font-medium mb-1">How to Import:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Click on a vertical from the list to open its edit view</li>
                  <li>Use the <strong>&quot;Import Data&quot;</strong> button in the header to upload a filled template</li>
                  <li>The import will update that specific vertical with the uploaded data</li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkImportDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ======================================================== */}
        {/* Environment Sync Dialog (preview ↔ production)           */}
        {/* ======================================================== */}
        <Dialog open={envSyncOpen} onOpenChange={setEnvSyncOpen}>
          <DialogContent className="max-w-2xl" data-testid="env-sync-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-purple-600" /> Environment Sync — Migrate Verticals Content</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2 text-sm">
              <div className="rounded-lg border border-purple-200 bg-purple-50/50 p-3 text-[13px] text-slate-700">
                Use this tool to copy all vertical-related content between two environments (e.g., from preview → production).
                <br /><br />
                <strong>What's included in the snapshot:</strong>
                <ul className="list-disc ml-5 mt-1 space-y-0.5 text-[12px]">
                  <li><strong>Verticals</strong> — Basic Info, Overview, Investment, Duration, Value Delivered, Before/After, Deliverables, Pricing/ROI Calculator, FAQ, Competitor Comparison, Implementation Timeline, Videos, Brochures, Testimonials, Image, Icon, Order</li>
                  <li><strong>Problem Statements</strong> — every problem under each vertical (title, description, target audience, expected outcome)</li>
                  <li><strong>Solutions</strong> — full per-problem solution payload (Overview, Steps, Pricing, Deliverables, FAQ, Videos, Before/After, Value Delivered)</li>
                  <li><strong>Case Studies</strong> — client wins linked to verticals</li>
                  <li><strong>Client Logos</strong> — brand logos shown on each vertical page</li>
                </ul>
                <br />
                <strong>Workflow:</strong>
                <ol className="list-decimal ml-5 mt-1 space-y-0.5">
                  <li>On the <em>source</em> environment (e.g., preview admin): click <strong>Download Snapshot</strong> — saves a JSON file.</li>
                  <li>Log in to the <em>target</em> environment admin (e.g., production).</li>
                  <li>Come back to this dialog, pick the JSON file, choose <strong>Merge</strong> (safe, default) or <strong>Replace</strong> (wipes target first), optionally run a <strong>Dry Run</strong> to preview counts.</li>
                </ol>
              </div>

              {/* STEP 1 — download */}
              <div className="border border-slate-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Step 1 — Export (run on source)</p>
                <Button onClick={handleDownloadSnapshot} disabled={envSyncBusy} className="gap-2 bg-[#0066CC] hover:bg-[#0052A3]" data-testid="env-sync-download">
                  {envSyncBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Download Snapshot (this environment)
                </Button>
              </div>

              {/* STEP 2 — upload */}
              <div className="border border-slate-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Step 2 — Import (run on target)</p>
                <input
                  type="file" accept=".json"
                  onChange={(e) => { setEnvSyncFile(e.target.files?.[0] || null); setEnvSyncResult(null); }}
                  className="text-xs w-full mb-3"
                  data-testid="env-sync-file"
                />
                {envSyncFile && (
                  <p className="text-[11px] text-slate-500 mb-2">Selected: <strong>{envSyncFile.name}</strong> ({Math.round(envSyncFile.size / 1024)} KB)</p>
                )}
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" disabled={!envSyncFile || envSyncBusy} onClick={() => handleUploadSnapshot("merge", true)} data-testid="env-sync-dryrun">
                    <Eye className="w-4 h-4 mr-1" /> Dry Run (preview)
                  </Button>
                  <Button disabled={!envSyncFile || envSyncBusy} onClick={() => handleUploadSnapshot("merge", false)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="env-sync-merge">
                    {envSyncBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                    Import — Merge (safe)
                  </Button>
                  <Button variant="destructive" disabled={!envSyncFile || envSyncBusy} onClick={() => {
                    if (window.confirm("REPLACE mode will DELETE all verticals, problem statements, and client logos on THIS environment before loading the snapshot. This cannot be undone. Continue?")) {
                      handleUploadSnapshot("replace", false);
                    }
                  }} data-testid="env-sync-replace">
                    <AlertCircle className="w-4 h-4 mr-1" /> Import — Replace (wipes first)
                  </Button>
                </div>
              </div>

              {/* Result summary */}
              {envSyncResult && (
                <div className="border-2 border-emerald-200 rounded-lg p-3 bg-emerald-50/60" data-testid="env-sync-result">
                  <p className="text-xs font-semibold text-emerald-700 uppercase mb-2">{envSyncResult.dry_run ? "Dry run — preview" : "Import complete"}</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
                    <span>Mode:</span><strong>{envSyncResult.mode}</strong>
                    <span>Incoming verticals:</span><strong>{envSyncResult.incoming.verticals}</strong>
                    <span>Incoming problems:</span><strong>{envSyncResult.incoming.problem_statements}</strong>
                    <span>Incoming solutions:</span><strong>{envSyncResult.incoming.solutions ?? 0}</strong>
                    <span>Incoming logos:</span><strong>{envSyncResult.incoming.client_logos}</strong>
                    <span>Incoming case studies:</span><strong>{envSyncResult.incoming.case_studies ?? 0}</strong>
                    <div className="col-span-2 h-px bg-emerald-200 my-1" />
                    <span>Verticals inserted / updated:</span><strong className="text-emerald-700">+{envSyncResult.actions.verticals_inserted} / ~{envSyncResult.actions.verticals_updated}</strong>
                    <span>Problems inserted / updated:</span><strong className="text-emerald-700">+{envSyncResult.actions.problems_inserted} / ~{envSyncResult.actions.problems_updated}</strong>
                    <span>Solutions inserted / updated:</span><strong className="text-emerald-700">+{envSyncResult.actions.solutions_inserted ?? 0} / ~{envSyncResult.actions.solutions_updated ?? 0}</strong>
                    <span>Case studies inserted / updated:</span><strong className="text-emerald-700">+{envSyncResult.actions.case_studies_inserted ?? 0} / ~{envSyncResult.actions.case_studies_updated ?? 0}</strong>
                    <span>Logos inserted / updated:</span><strong className="text-emerald-700">+{envSyncResult.actions.logos_inserted} / ~{envSyncResult.actions.logos_updated}</strong>
                    {(envSyncResult.actions.verticals_deleted > 0 || envSyncResult.actions.solutions_deleted > 0) && (
                      <>
                        <div className="col-span-2 h-px bg-red-200 my-1" />
                        <span className="text-red-700">Deleted (replace mode):</span>
                        <strong className="text-red-700">
                          {envSyncResult.actions.verticals_deleted}V / {envSyncResult.actions.problems_deleted}P / {envSyncResult.actions.solutions_deleted ?? 0}S / {envSyncResult.actions.logos_deleted}L / {envSyncResult.actions.case_studies_deleted ?? 0}CS
                        </strong>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEnvSyncOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
